import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Paperclip } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState, KpiCard } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { inr, dmy, today } from "@/lib/format";

const EMPTY = { vendor: "", title: "", category: "", amount: "", expense_date: today(),
  payment_method: "cash", reference_no: "", notes: "", attachment_name: "", attachment_data: "" };

export default function HospitalExpenses() {
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState("");

  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/hospital/expenses");
      setItems(data.items); setVendors(data.vendors);
    } catch (e) { setError(apiError(e)); }
  }, []);

  useEffect(() => { if (can("hospital.expenses.manage")) load(); }, [load, can]);
  if (!can("hospital.expenses.manage")) return <NoAccess what="hospital expenses" />;

  const attach = (file) => {
    if (!file) return;
    const ok = /(pdf|png|jpe?g|sheet|excel|csv)/i.test(file.type) || /\.(xlsx|xls|csv)$/i.test(file.name);
    if (!ok) return toast.error("Upload a PDF, PNG, JPEG, Excel or CSV file.");
    if (file.size > 3 * 1024 * 1024) return toast.error("Attachment must be under 3 MB.");
    const r = new FileReader();
    r.onload = () => setForm((f) => ({ ...f, attachment_name: file.name, attachment_data: r.result }));
    r.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault(); setFormError("");
    try {
      await api.post("/hospital/expenses", { ...form, amount: Number(form.amount) });
      toast.success("Expense recorded successfully.");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { setFormError(apiError(err)); }
  };

  const openAttachment = async (row) => {
    try {
      const { data } = await api.get(`/hospital/expenses/${row.expense_id}/attachment`);
      const w = window.open();
      if (!w) return;
      // Safely create an iframe element instead of using document.write (avoids XSS via document streams).
      try {
        const iframe = w.document.createElement("iframe");
        // data.data is expected to be a data: URL or safe URL from our backend. Assigning to src is safe here.
        iframe.setAttribute("src", data.data);
        iframe.setAttribute("style", "border:0;width:100%;height:100%");
        iframe.setAttribute("title", row.attachment_name || "attachment");
        iframe.setAttribute("loading", "eager");
        // Optional sandbox could be applied if attachments are known to be safe images/PDFs.
        // Clear existing children without using innerHTML to avoid XSS sink.
        const doc = w.document;
        let body = doc.body || doc.getElementsByTagName("body")[0];
        if (!body) {
          body = doc.createElement("body");
          if (doc.documentElement) doc.documentElement.appendChild(body);
        }
        while (body.firstChild) body.removeChild(body.firstChild);
        body.appendChild(iframe);
      } catch (err) {
        // Fallback: navigate the opened window to the URL so the browser handles it.
        try { w.location.href = data.data; } catch (_) { /* ignore */ }
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="hospital-expenses-page">
      <PageHeader overline="Hospital" title="Expenses"
        subtitle="Vendor-wise expenses with free-text categories and invoice attachments. Included in revenue and profit reporting."
        actions={<button onClick={() => setOpen(true)} className={primaryBtnCls} data-testid="add-hospital-expense-btn">Add expense</button>} />
      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <>
          <div className="grid gap-4 sm:grid-cols-3 mb-8">
            <KpiCard label="Entries" value={items.length} onClick={() => {}} testid="hexp-count" />
            <KpiCard label="Total recorded" value={inr(items.reduce((s, e) => s + e.amount, 0))} tone="accent" onClick={() => {}} testid="hexp-total" />
            <KpiCard label="Vendors" value={vendors.length} onClick={() => {}} testid="hexp-vendors" />
          </div>
          <Table testid="hospital-expenses-table"
            columns={["Date", "Vendor", "Expense", "Category", "Method", "Reference", "Added by", "Amount", ""]}
            rows={items} empty={<EmptyState title="No expenses recorded" subtitle="Track consumables, salaries, utilities and vendor bills." testid="hexp-empty" />}
            renderRow={(e) => (
              <tr key={e.expense_id} data-testid={`hexp-row-${e.expense_id}`}>
                <Td className="text-slate-700 whitespace-nowrap">{dmy(e.expense_date)}</Td>
                <Td className="text-slate-700">{e.vendor || "—"}</Td>
                <Td className="font-semibold text-slate-900">{e.title}</Td>
                <Td className="text-slate-700">{e.category}</Td>
                <Td className="text-slate-700 capitalize">{e.payment_method}</Td>
                <Td className="text-slate-600">{e.reference_no || "—"}</Td>
                <Td className="text-slate-600">{e.added_by || "—"}</Td>
                <Td className="text-slate-900 font-semibold">{inr(e.amount)}</Td>
                <Td className="text-right">
                  {e.attachment_name && (
                    <button onClick={() => openAttachment(e)} className={ghostBtnCls} data-testid={`hexp-attach-${e.expense_id}`}>
                      <Paperclip className="h-4 w-4" /> View
                    </button>
                  )}
                </Td>
              </tr>
            )} />
        </>
      )}

      <Modal open={open} onClose={() => setOpen(false)} wide title="Add hospital expense"
        subtitle="Categories are free text — enter whatever suits your hospital." testid="add-hospital-expense-modal">
        <form onSubmit={submit} className="grid gap-5 sm:grid-cols-3">
          <Field label="Vendor">
            <input className={inputCls} list="vendor-list" data-testid="hexp-vendor-input" value={form.vendor}
              onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="e.g. ABC Medical Supplies" />
            <datalist id="vendor-list">{vendors.map((v) => <option key={v.vendor_id} value={v.name} />)}</datalist>
          </Field>
          <Field label="Expense / product name *"><input required className={inputCls} data-testid="hexp-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="First aid supplies" /></Field>
          <Field label="Category *"><input required className={inputCls} data-testid="hexp-category-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Medical consumables" /></Field>
          <Field label="Amount (₹) *"><input required type="number" className={inputCls} data-testid="hexp-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></Field>
          <Field label="Date *"><input required type="date" className={inputCls} data-testid="hexp-date-input" value={form.expense_date} onChange={(e) => setForm({ ...form, expense_date: e.target.value })} /></Field>
          <Field label="Payment method">
            <select className={inputCls} value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
              {["cash", "card", "upi", "netbanking", "cheque", "other"].map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Invoice / reference no."><input className={inputCls} value={form.reference_no} onChange={(e) => setForm({ ...form, reference_no: e.target.value })} /></Field>
          <Field label="Attachment (PDF, image, Excel)" className="sm:col-span-2">
            <input type="file" accept=".pdf,.png,.jpg,.jpeg,.xlsx,.xls,.csv" data-testid="hexp-attachment-input"
              onChange={(e) => attach(e.target.files[0])} className="text-sm text-slate-700" />
            {form.attachment_name && <p className="mt-2 text-xs text-slate-600">{form.attachment_name}</p>}
          </Field>
          <Field label="Notes" className="sm:col-span-3"><textarea rows={2} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          {formError && <p className="sm:col-span-3 text-sm text-red-600" data-testid="hexp-form-error">{formError}</p>}
          <div className="sm:col-span-3"><button className={primaryBtnCls} data-testid="hexp-submit-btn">Save expense</button></div>
        </form>
      </Modal>
    </div>
  );
}
